import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { DecisionConfirmModal } from '../shared/index.js';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { governanceActionErrorToast } from '../../../utils/governanceActionToast.js';
import { isDecisionSkipped } from '../../../utils/decisionSkipped.js';
import { Check, X, Pencil, HelpCircle } from 'lucide-react';

/**
 * Gov Change Request decision for an interim `change_request` event (CT-5095),
 * modeled on the HTL prototype EVT-012 "Truth Conflict Resolve":
 *
 *  - Accept change  → change-request/resolve { approve }  (card leaves the inbox)
 *  - Reject         → change-request/resolve { reject }   (card leaves the inbox)
 *  - Propose correction → opens the body's inline editor (prefilled with the
 *    proposed text); Submit files a counter-proposal via create-on-change-request.
 *    The parent CR stays open — `resolve` only supports approve/reject, so there
 *    is no one-step "accept with modifications" (backend gap, honest wording here).
 *  - Request more information → the Ask flow (thread), no governance mutation.
 *
 * Success gating mirrors {@link completeTask}: the provider only marks the event
 * resolved on success, so a failure (e.g. approve → 500, CT-4867) leaves the card
 * in the inbox to retry with a controlled error toast.
 */

const SUCCESS_COPY = {
  approve: { title: 'changeRequest.approvedTitle', message: 'changeRequest.approvedMessage' },
  reject: { title: 'changeRequest.rejectedTitle', message: 'changeRequest.rejectedMessage' },
  correct: { title: 'changeRequest.correctedTitle', message: 'changeRequest.correctedMessage' },
};

function ChangeRequestDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { resolveChangeRequestEvent, pendingDecisionIds } = useWorkQueueContext();
  const { editing, draft, startEdit, cancelEdit } = useEventDraft();
  const isResolving = pendingDecisionIds?.has(event.id) ?? false;
  const proposed = event.detail?.proposedStatement ?? event.situation ?? '';
  // CT-5334: Reject must always surface a reason field — text is optional, the
  // field itself is not. Scoped to Review only (this component); Promotion is
  // untouched. Backend's resolve contract has no `reason` field yet (only
  // `changeRequestUuid` + `action`) — sent anyway since the schema silently
  // ignores unknown fields, so this is ready the moment backend adds storage.
  const [rejectOpen, setRejectOpen] = useState(false);

  const run = useCallback(
    async (params, action) => {
      if (isResolving) return;
      try {
        await resolveChangeRequestEvent(event.id, params);
        const copy = SUCCESS_COPY[action];
        toast.success({ title: t(copy.title), message: t(copy.message) });
        if (action === 'correct') cancelEdit();
        return true;
      } catch (err) {
        if (isDecisionSkipped(err)) return true;
        toast.error(governanceActionErrorToast(t, err, action));
        return false;
      }
    },
    [event.id, isResolving, resolveChangeRequestEvent, t, toast, cancelEdit],
  );

  const handleAccept = useCallback(() => run({ action: 'approve' }, 'approve'), [run]);
  const openReject = useCallback(() => setRejectOpen(true), []);
  const closeReject = useCallback(() => setRejectOpen(false), []);
  // Reject confirms through DecisionConfirmModal (CT-5334), which only keeps
  // itself open for a retry when onConfirm's promise rejects — same contract
  // every other Decision component's confirm modal relies on (BreakGlass,
  // Continuation, ClaimSuggestion, Question, Task). `run` itself stays
  // fire-and-forget safe for handleAccept/handleSubmitCorrection (neither is
  // awaited), so only this modal-driven path turns a `false` result into a
  // throw — a plain rethrow from `run` would leave those two unawaited calls
  // as unhandled promise rejections.
  const handleConfirmReject = useCallback(
    async ({ reason } = {}) => {
      const ok = await run({ action: 'reject', reason: reason || undefined }, 'reject');
      if (!ok) throw new Error('Failed to reject the change request.');
    },
    [run],
  );
  const handleSubmitCorrection = useCallback(() => {
    const text = (draft ?? '').trim();
    if (!text) return;
    run({ action: 'correct', correction: text }, 'correct');
  }, [draft, run]);

  const primaryActions = useMemo(() => {
    if (editing) {
      return [
        {
          id: 'cancel',
          label: t('actions.cancel'),
          size: 'md',
          variant: 'secondary',
          disabled: isResolving,
          onClick: cancelEdit,
        },
        {
          id: 'submit-correction',
          label: isResolving ? t('governance.resolving') : t('changeRequest.submitCorrection'),
          size: 'md',
          disabled: isResolving || !(draft ?? '').trim(),
          onClick: handleSubmitCorrection,
        },
      ];
    }
    return [
      {
        id: 'accept',
        label: isResolving ? t('governance.resolving') : t('changeRequest.acceptChange'),
        size: 'md',
        icon: Check,
        disabled: isResolving,
        onClick: handleAccept,
      },
      {
        id: 'correct',
        label: t('changeRequest.proposeCorrection'),
        size: 'md',
        icon: Pencil,
        variant: 'secondary',
        disabled: isResolving,
        onClick: () => startEdit(proposed),
      },
      {
        id: 'reject',
        label: t('changeRequest.reject'),
        size: 'md',
        icon: X,
        variant: 'danger',
        disabled: isResolving,
        onClick: openReject,
      },
      {
        id: 'request-info',
        label: t('changeRequest.requestMoreInfo'),
        size: 'md',
        icon: HelpCircle,
        variant: 'secondary',
        disabled: isResolving,
        onClick: () => actions.ask?.(event),
      },
    ];
  }, [
    editing,
    isResolving,
    draft,
    t,
    cancelEdit,
    handleSubmitCorrection,
    handleAccept,
    openReject,
    startEdit,
    proposed,
    actions,
    event,
  ]);

  return (
    <>
      <DecisionFooter
        event={event}
        primaryActions={primaryActions}
        onSkip={actions.skip}
        onAsk={actions.ask}
        onEscalate={actions.escalate}
        onTrace={actions.trace}
        extraMenuItems={isResolving || editing ? [] : actions.extraMenuItems}
        sticky={actions.sticky}
      />

      <DecisionConfirmModal
        isOpen={rejectOpen}
        onClose={closeReject}
        icon={<X size={20} className="text-rose-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('changeRequest.rejectConfirmTitle')}
        subtitle={t('changeRequest.rejectConfirmSubtitle')}
        showReason
        reasonLabel={t('changeRequest.rejectReasonLabel')}
        reasonPlaceholder={t('changeRequest.rejectReasonPlaceholder')}
        confirmLabel={t('changeRequest.confirmReject')}
        confirmVariant="danger"
        onConfirm={handleConfirmReject}
      />
    </>
  );
}

export default ChangeRequestDecision;
